define('OutSystems/ClientRuntime/modules/Database/IndexedDB/IndexedDBDatabase',["require", "exports", "tslib", "../../../Exceptions", "../../../Logger", "../../Common/BrowserUtils", "../Common/Database", "./IndexedDBTransaction", "./IndexedDBTransactionWithoutObjectStores", "./IndexedDBUtils"], function (require, exports, tslib_1, Exceptions_1, Logger, BrowserUtils_1, Database_1, IndexedDBTransaction_1, IndexedDBTransactionWithoutObjectStores_1, IndexedDBUtils_1) {
    "use strict";
    Object.defineProperty(exports, "__esModule", { value: true });
    var COULD_NOT_OPEN_DATABASE_MSG = "Could not open database";
    var IndexedDBDatabase = (function (_super) {
        tslib_1.__extends(IndexedDBDatabase, _super);
        function IndexedDBDatabase(databaseName, databaseImplementation, onNewDatabaseVersionDetected) {
            var _this = _super.call(this, databaseName) || this;
            _this.databaseImplementation = databaseImplementation;
            _this.onNewDatabaseVersionDetected = onNewDatabaseVersionDetected;
            _this.setOnVersionChangeEvent();
            return _this;
        }
        IndexedDBDatabase.asSafeDbName = function (databaseName) {
            return databaseName || "";
        };
        IndexedDBDatabase.executeTransactionPromiseWithRetry = function (promiseBody, maxExecuteCount) {
            if (maxExecuteCount === void 0) { maxExecuteCount = 3; }
            return tslib_1.__awaiter(this, void 0, void 0, function () {
                var execute, executeCount, result, e_1, err;
                return tslib_1.__generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            execute = true;
                            executeCount = 0;
                            _a.label = 1;
                        case 1:
                            if (!execute) return [3, 6];
                            executeCount++;
                            _a.label = 2;
                        case 2:
                            _a.trys.push([2, 4, , 5]);
                            return [4, new Promise(promiseBody)];
                        case 3:
                            result = _a.sent();
                            execute = false;
                            return [3, 5];
                        case 4:
                            e_1 = _a.sent();
                            err = e_1;
                            if (!/the transaction is inactive or finished/i.test(err.message) || executeCount >= maxExecuteCount) {
                                throw e_1;
                            }
                            return [3, 5];
                        case 5: return [3, 1];
                        case 6: return [2, result];
                    }
                });
            });
        };
        IndexedDBDatabase.open = function (databaseName, onNewDatabaseVersionDetected) {
            var _this = this;
            databaseName = this.asSafeDbName(databaseName);
            return new Promise(function (resolve, reject) {
                if (!_this.cache[databaseName]) {
                    if (typeof window === "undefined") {
                        return reject(new Exceptions_1.Exceptions.SystemException("Database is only available in the browser/web view"));
                    }
                    if (!("indexedDB" in window)) {
                        Logger.trace(IndexedDBDatabase, "This browser doesn't support IndexedDB");
                        return reject(new Exceptions_1.Exceptions.SystemException("This browser doesn't support IndexedDB"));
                    }
                    if (BrowserUtils_1.isSafari(navigator.userAgent) && BrowserUtils_1.windowInsideIframe(window)) {
                        var errorMessage = "Your app requires native access to a Local Storage database that is not available in your browser while using an iframe.\nPlease test your app in your mobile device or use Google Chrome";
                        alert(errorMessage);
                        return reject(new Exceptions_1.Exceptions.SystemException(errorMessage));
                    }
                    Logger.trace(IndexedDBDatabase, "Opening database '" + databaseName + "' on IndexedDB");
                    var database_1 = window.indexedDB.open(databaseName);
                    database_1.onsuccess = function () {
                        Logger.trace(IndexedDBDatabase, "Database '" + databaseName + "' opened successfully with version '" + database_1.result.version + "'");
                        var db = database_1.result;
                        _this.cache[databaseName] = new IndexedDBDatabase(databaseName, db, onNewDatabaseVersionDetected);
                        resolve(_this.cache[databaseName]);
                    };
                    database_1.onerror = function () {
                        Logger.trace(IndexedDBDatabase, "Error opening database '" + databaseName + "'");
                        reject(new Exceptions_1.Exceptions.OpenDatabaseException(COULD_NOT_OPEN_DATABASE_MSG + " '" + databaseName + "'"));
                    };
                }
                else {
                    resolve(_this.cache[databaseName]);
                }
            });
        };
        IndexedDBDatabase.prototype.setOnVersionChangeEvent = function () {
            var _this = this;
            this.databaseImplementation.onversionchange = function () {
                _this.databaseImplementation.close();
                if (_this.onNewDatabaseVersionDetected) {
                    _this.onNewDatabaseVersionDetected();
                }
            };
        };
        IndexedDBDatabase.prototype.close = function () {
            Logger.trace(this, "Closing database " + this.name + " with version '" + this.databaseImplementation.version + "'");
            this.databaseImplementation.close();
            delete IndexedDBDatabase.cache[this.name];
            return Promise.resolve();
        };
        IndexedDBDatabase.prototype.testConnection = function () {
            return Promise.resolve();
        };
        IndexedDBDatabase.prototype.executeQuery = function (body, allowTraces, affectedTables) {
            if (allowTraces === void 0) { allowTraces = true; }
            return this.executeReadTransaction(function (abstractTransaction) { return abstractTransaction.executeQuery(body); }, allowTraces, affectedTables);
        };
        IndexedDBDatabase.prototype.executeTransaction = function (body, allowTraces, affectedTables) {
            if (allowTraces === void 0) { allowTraces = true; }
            return this.innerExecuteDMLTransaction(body, allowTraces, "readwrite", affectedTables);
        };
        IndexedDBDatabase.prototype.executeReadTransaction = function (body, allowTraces, affectedTables) {
            if (allowTraces === void 0) { allowTraces = true; }
            return this.innerExecuteDMLTransaction(body, allowTraces, "readonly", affectedTables);
        };
        IndexedDBDatabase.prototype.innerExecuteDMLTransaction = function (body, allowTraces, mode, affectedTables) {
            if (this.databaseImplementation.objectStoreNames.length === 0) {
                return this.innerExecuteIDBTransactionWithoutObjectStores(body, allowTraces);
            }
            else {
                return this.innerExecuteIDBTransaction(body, allowTraces, mode, affectedTables);
            }
        };
        IndexedDBDatabase.prototype.innerExecuteIDBTransaction = function (body, allowTraces, mode, affectedTables) {
            var _this = this;
            var txBody = function (resolve, reject) { return tslib_1.__awaiter(_this, void 0, void 0, function () {
                var result, caughtError, storeArray, i, idbTx, tx, error_1;
                var _this = this;
                return tslib_1.__generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            result = null;
                            caughtError = null;
                            storeArray = affectedTables ? affectedTables.map(function (t) { return t.tableName; }) : [];
                            if (storeArray.length === 0) {
                                for (i = 0; i < this.databaseImplementation.objectStoreNames.length; i++) {
                                    storeArray.push(this.databaseImplementation.objectStoreNames.item(i));
                                }
                            }
                            idbTx = this.databaseImplementation.transaction(storeArray, mode);
                            tx = new IndexedDBTransaction_1.default(allowTraces, idbTx, this.databaseImplementation);
                            tx.onComplete = function () { return tslib_1.__awaiter(_this, void 0, void 0, function () {
                                return tslib_1.__generator(this, function (_a) {
                                    switch (_a.label) {
                                        case 0: return [4, IndexedDBUtils_1.loadBinaryDataContent(result)];
                                        case 1:
                                            _a.sent();
                                            resolve(result);
                                            return [2];
                                    }
                                });
                            }); };
                            tx.onAbort = function (error) { return reject(caughtError || error); };
                            _a.label = 1;
                        case 1:
                            _a.trys.push([1, 3, , 4]);
                            return [4, body(tx)];
                        case 2:
                            result = _a.sent();
                            return [3, 4];
                        case 3:
                            error_1 = _a.sent();
                            caughtError = error_1;
                            idbTx === null || idbTx === void 0 ? void 0 : idbTx.abort();
                            return [3, 4];
                        case 4: return [2];
                    }
                });
            }); };
            return IndexedDBDatabase.executeTransactionPromiseWithRetry(txBody);
        };
        IndexedDBDatabase.prototype.innerExecuteIDBTransactionWithoutObjectStores = function (body, allowTraces) {
            var tx = new IndexedDBTransactionWithoutObjectStores_1.default(allowTraces, this.databaseImplementation);
            return body(tx);
        };
        IndexedDBDatabase.prototype.executeUpgradeTransaction = function (body, allowTraces) {
            if (allowTraces === void 0) { allowTraces = true; }
            return tslib_1.__awaiter(this, void 0, void 0, function () {
                var _this = this;
                return tslib_1.__generator(this, function (_a) {
                    return [2, new Promise(function (resolve, reject) {
                            var result = null;
                            var caughtError = null;
                            var version = _this.databaseImplementation.version;
                            _this.databaseImplementation.close();
                            var openRequest = window.indexedDB.open(_this.name, version + 1);
                            openRequest.onsuccess = function () {
                                _this.trace("Database '" + _this.name + "' opened successfully with version '" + (version + 1) + "'", allowTraces);
                                _this.databaseImplementation = openRequest.result;
                                _this.setOnVersionChangeEvent();
                                return resolve(result);
                            };
                            openRequest.onerror = function () {
                                _this.trace("Error opening '" + _this.name + "' database with version '" + (version + 1) + "'", allowTraces);
                                return reject(caughtError || openRequest.error);
                            };
                            openRequest.onupgradeneeded = function () {
                                _this.trace("Upgrading database '" + _this.name + "' with version '" + (version + 1) + "'", allowTraces);
                                _this.databaseImplementation = openRequest.result;
                                var tx = new IndexedDBTransaction_1.default(allowTraces, openRequest.transaction, openRequest.result);
                                tx.onAbort = function (error) { return (caughtError = caughtError || error); };
                                body(tx)
                                    .then(function (bodyResult) { return (result = bodyResult); })
                                    .catch(function (error) {
                                    caughtError = error;
                                    openRequest.transaction.abort();
                                });
                            };
                        })];
                });
            });
        };
        IndexedDBDatabase.prototype.trace = function (msg, allowTraces) {
            if (allowTraces === void 0) { allowTraces = true; }
            if (allowTraces) {
                Logger.trace(this, msg);
            }
        };
        IndexedDBDatabase.cache = {};
        return IndexedDBDatabase;
    }(Database_1.default));
    exports.default = IndexedDBDatabase;
});
//# sourceMappingURL=IndexedDBDatabase.js.map;
